'use client';

import { fetchBlogs } from '@eliadmin/core/src/api/blogs/blogs-api';
import { CacheKey } from '@eliadmin/core/src/api/constants';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useInfiniteQuery } from '@tanstack/react-query';
import { BlogCardSkeleton } from './blog-cart-skeleton';
import { BlogCard } from './blog-card';
import { Paragraph } from '../ui/paragraph';
import { Button } from '../ui/button';

export function BlogsList({ locale }: { locale: string }) {
  const { t } = useTranslations();

  const blogsQuery = useInfiniteQuery({
    queryKey: [CacheKey.BLOGS, locale],
    queryFn: async ({ pageParam = 1 }) => {
      return await fetchBlogs({
        locale,
        query: {
          page: pageParam,
          results: 9,
          order: 'desc',
          orderBy: 'created_at',
        },
      });
    },
    getNextPageParam: (lastPage: any) => {
      if (lastPage?.meta?.current_page < lastPage?.meta?.last_page)
        return lastPage?.meta?.current_page + 1;
      return undefined;
    },
    initialPageParam: 1,
  });

  const blogs = blogsQuery.data?.pages.flatMap((page: any) => page.data) ?? [];
  const isLoading = blogsQuery?.isLoading;

  return (
    <section
      className={cn(
        'custom-section',
        'generic-gap',
        'pb-16 pt-8 sm:pb-20 sm:pt-10 lg:pb-24 lg:pt-12',
      )}
    >
      <div
        className={cn(
          'custom-container',
          'grid grid-cols-1 gap-x-3 gap-y-6 sm:grid-cols-2 md:gap-y-8 lg:grid-cols-3 lg:gap-y-12',
        )}
      >
        {blogsQuery.error && (
          <Paragraph className="col-span-1 pt-8 text-center md:col-span-2">
            {t('text2364')}
          </Paragraph>
        )}
        {isLoading ? (
          [...Array(6)].map((_, index) => <BlogCardSkeleton key={index} />)
        ) : blogs.length > 0 ? (
          <>
            {blogs.map(blog => (
              <BlogCard key={blog.id} blog={blog} headingTag="h2" />
            ))}
          </>
        ) : (
          <Paragraph
            color="muted-foreground"
            size="lg"
            className="col-span-1 text-center md:col-span-2"
          >
            {t('text2376')}
          </Paragraph>
        )}
      </div>
      {blogsQuery.hasNextPage && (
        <div className="mt-4 flex w-full items-center justify-center pr-4 md:pr-0">
          <Button
            onClick={() => blogsQuery.fetchNextPage()}
            variant={'primary-foreground'}
            className="w-full sm:w-fit"
          >
            {t('text338')}
          </Button>
        </div>
      )}
    </section>
  );
}
